import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiChevronRight,
  FiCopy,
  FiEdit2,
  FiLock,
  FiMail,
  FiMapPin,
  FiPhone,
  FiShoppingBag,
  FiTag,
  FiUser
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

const INDIAN_PHONE_RE = /^[6-9]\d{9}$/;

const Profile = () => {
  const { user, loading, isAuthenticated, updateProfile, isDashboardRoute, getToken } = useAuth();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Change password section
  const [pwData, setPwData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [copyState, setCopyState] = useState({ ref: false, referral: false });

  useEffect(() => {
    if (!user) return;
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || ''
    });
  }, [user]);

  const fullName = useMemo(() => {
    const first = user?.first_name || '';
    const last = user?.last_name || '';
    return `${first} ${last}`.trim() || 'Account User';
  }, [user]);

  const initials = useMemo(() => {
    const first = (user?.first_name || '').trim().charAt(0).toUpperCase();
    const last = (user?.last_name || '').trim().charAt(0).toUpperCase();
    return `${first}${last}`.trim() || 'U';
  }, [user]);

  const profileCompletion = useMemo(() => {
    const checks = [
      Boolean((user?.first_name || '').trim()),
      Boolean((user?.last_name || '').trim()),
      Boolean((user?.email || '').trim()),
      Boolean((user?.phone || '').trim()),
      Boolean((user?.user_ref_no || '').trim()),
      Boolean((user?.referral_code || '').trim())
    ];
    const completeCount = checks.filter(Boolean).length;
    return Math.round((completeCount / checks.length) * 100);
  }, [user]);

  const userRef = user?.user_ref_no || 'Generating...';
  const referralCode = user?.referral_code || 'Generating...';

  const copyText = async (value, type) => {
    if (!value || value === 'Generating...' || !navigator?.clipboard) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopyState((prev) => ({ ...prev, [type]: true }));
      setTimeout(() => {
        setCopyState((prev) => ({ ...prev, [type]: false }));
      }, 1200);
    } catch {
      setError('Unable to copy right now.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handlePwChange = (e) => {
    const { name, value } = e.target;
    setPwData((prev) => ({ ...prev, [name]: value }));
    if (pwError) setPwError('');
    if (pwSuccess) setPwSuccess('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');
    const { current_password, new_password, confirm_password } = pwData;
    if (!current_password || !new_password || !confirm_password) {
      setPwError('All password fields are required.');
      return;
    }
    if (new_password.length < 8) {
      setPwError('New password must be at least 8 characters.');
      return;
    }
    if (new_password !== confirm_password) {
      setPwError('New password and confirmation do not match.');
      return;
    }
    setPwSaving(true);
    try {
      const API_BASE = process.env.REACT_APP_API_URL || '';
      const token = getToken(isDashboardRoute);
      const res = await fetch(`${API_BASE}/api/auth/change-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ current_password, new_password })
      });
      const data = await res.json();
      if (!data.success) {
        setPwError(data.message || 'Failed to change password.');
      } else {
        setPwSuccess('Password changed successfully.');
        setPwData({ current_password: '', new_password: '', confirm_password: '' });
      }
    } catch {
      setPwError('Unable to change password right now. Please try again.');
    }
    setPwSaving(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const firstName = formData.first_name.trim();
    const lastName = formData.last_name.trim();
    const phone = formData.phone.trim();

    if (!firstName || !lastName) {
      setError('First name and last name are required.');
      return;
    }

    if (phone && !INDIAN_PHONE_RE.test(phone)) {
      setError('Enter a valid 10-digit Indian mobile number (starting with 6–9).');
      return;
    }

    setSaving(true);
    const response = await updateProfile(
      {
        first_name: firstName,
        last_name: lastName,
        phone
      },
      isDashboardRoute
    );
    setSaving(false);

    if (!response.success) {
      setError(response.message || 'Unable to update account right now.');
      return;
    }

    setSuccess('Account details updated successfully.');
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <p className="text-gray-600">Loading account...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h1 className="text-2xl font-semibold text-gray-900">Account</h1>
          <p className="mt-2 text-gray-600">Please log in to view and update your account details.</p>
          <Link
            to="/login"
            className="inline-flex mt-4 px-4 py-2 rounded-lg text-white font-medium"
            style={{ backgroundColor: '#4F46E5' }}
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#f3f5f8] py-4 sm:py-8 min-h-screen">
      <div className="container mx-auto px-3 sm:px-4">
        <div className="max-w-3xl mx-auto space-y-4 sm:space-y-5">
          <section className="rounded-3xl bg-white border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 sm:p-6">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-full bg-slate-100 text-slate-700 font-semibold flex items-center justify-center shrink-0">
                    {initials}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xl font-semibold text-slate-900 truncate">{fullName}</p>
                    <p className="text-sm text-slate-600 truncate">{user.email}</p>
                  </div>
                </div>
                <a
                  href="#profile-edit"
                  className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                >
                  <FiEdit2 className="h-4 w-4" />
                  Edit
                </a>
              </div>

              <div className="mt-4">
                <div className="flex items-center justify-between text-xs text-slate-600 mb-1.5">
                  <span>Profile completion</span>
                  <span className="font-semibold text-slate-700">{profileCompletion}%</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-amber-400 to-yellow-300 transition-all"
                    style={{ width: `${profileCompletion}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-[#f6fbff] to-[#f4fff9] border-t border-slate-100 flex items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Member Benefits</p>
                <p className="text-sm font-semibold text-slate-800">Save more with offers and faster support</p>
              </div>
              <Link
                to="/categories"
                className="inline-flex items-center gap-1 rounded-full bg-amber-300 text-slate-900 px-3 py-1.5 text-xs font-semibold hover:bg-amber-200 transition-colors"
              >
                Explore
                <FiChevronRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </section>

          <section className="grid grid-cols-2 gap-3 sm:gap-4">
            <Link
              to="/orders"
              className="rounded-2xl bg-white border border-slate-200 p-4 flex items-center gap-3 hover:shadow-sm transition-shadow"
            >
              <span className="h-9 w-9 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center">
                <FiShoppingBag className="h-4.5 w-4.5" />
              </span>
              <span className="text-sm sm:text-base font-semibold text-slate-900">Orders</span>
            </Link>

            <a
              href="#profile-edit"
              className="rounded-2xl bg-white border border-slate-200 p-4 flex items-center gap-3 hover:shadow-sm transition-shadow"
            >
              <span className="h-9 w-9 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center">
                <FiUser className="h-4.5 w-4.5" />
              </span>
              <span className="text-sm sm:text-base font-semibold text-slate-900">Personal Info</span>
            </a>

            <a
              href="#profile-security"
              className="rounded-2xl bg-white border border-slate-200 p-4 flex items-center gap-3 hover:shadow-sm transition-shadow"
            >
              <span className="h-9 w-9 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center">
                <FiLock className="h-4.5 w-4.5" />
              </span>
              <span className="text-sm sm:text-base font-semibold text-slate-900">Security</span>
            </a>

            <Link
              to="/about"
              className="rounded-2xl bg-white border border-slate-200 p-4 flex items-center gap-3 hover:shadow-sm transition-shadow"
            >
              <span className="h-9 w-9 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center">
                <FiMapPin className="h-4.5 w-4.5" />
              </span>
              <span className="text-sm sm:text-base font-semibold text-slate-900">Help Center</span>
            </Link>
          </section>

          <section className="rounded-3xl bg-white border border-slate-200 p-4 sm:p-6 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base sm:text-lg font-semibold text-slate-900">Identity & Referral</h2>
              <span className="text-xs text-slate-500">Visible for every user</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs text-slate-500 mb-1">Reference ID</p>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-slate-900 break-all">{userRef}</p>
                  <button
                    type="button"
                    onClick={() => copyText(userRef, 'ref')}
                    className="shrink-0 rounded-lg px-2.5 py-1.5 text-xs font-medium bg-white border border-slate-200 text-slate-700 hover:bg-slate-100"
                  >
                    {copyState.ref ? 'Copied' : <FiCopy className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs text-slate-500 mb-1">Referral Code</p>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-slate-900 break-all">{referralCode}</p>
                  <button
                    type="button"
                    onClick={() => copyText(referralCode, 'referral')}
                    className="shrink-0 rounded-lg px-2.5 py-1.5 text-xs font-medium bg-white border border-slate-200 text-slate-700 hover:bg-slate-100"
                  >
                    {copyState.referral ? 'Copied' : <FiCopy className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section id="profile-edit" className="rounded-3xl bg-white border border-slate-200 p-4 sm:p-6 shadow-sm">
            <h2 className="text-base sm:text-lg font-semibold text-slate-900">My Account</h2>
            <p className="text-sm text-slate-600 mt-1 mb-4">Keep your details updated for faster checkout and support.</p>

            {error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="first_name" className="block text-sm font-medium text-slate-700 mb-1">
                    First Name
                  </label>
                  <input
                    id="first_name"
                    name="first_name"
                    type="text"
                    value={formData.first_name}
                    onChange={handleChange}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="last_name" className="block text-sm font-medium text-slate-700 mb-1">
                    Last Name
                  </label>
                  <input
                    id="last_name"
                    name="last_name"
                    type="text"
                    value={formData.last_name}
                    onChange={handleChange}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                  Email
                </label>
                <div className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-100 text-slate-600 flex items-center gap-2">
                  <FiMail className="h-4 w-4" />
                  <span className="truncate">{user.email || ''}</span>
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1">
                  Phone
                </label>
                <div className="relative">
                  <FiPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 h-4 w-4" />
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter 10-digit phone number"
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400"
                  />
                </div>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full sm:w-auto px-5 py-2.5 rounded-xl text-slate-900 font-semibold disabled:opacity-70 disabled:cursor-not-allowed bg-gradient-to-r from-amber-300 to-yellow-300 hover:from-amber-200 hover:to-yellow-200"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </section>

          <section id="profile-security" className="rounded-3xl bg-white border border-slate-200 p-4 sm:p-6 shadow-sm">
            <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-1">Security Settings</h2>
            <p className="text-sm text-slate-600 mb-4">Change your password regularly to keep your account secure.</p>

            {pwError && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {pwError}
              </div>
            )}
            {pwSuccess && (
              <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                {pwSuccess}
              </div>
            )}

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label htmlFor="current_password" className="block text-sm font-medium text-slate-700 mb-1">Current Password</label>
                <input
                  id="current_password"
                  name="current_password"
                  type="password"
                  value={pwData.current_password}
                  onChange={handlePwChange}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-amber-300"
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="new_password" className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
                  <input
                    id="new_password"
                    name="new_password"
                    type="password"
                    value={pwData.new_password}
                    onChange={handlePwChange}
                    minLength={8}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-amber-300"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="confirm_password" className="block text-sm font-medium text-slate-700 mb-1">Confirm New Password</label>
                  <input
                    id="confirm_password"
                    name="confirm_password"
                    type="password"
                    value={pwData.confirm_password}
                    onChange={handlePwChange}
                    minLength={8}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-amber-300"
                    required
                  />
                </div>
              </div>
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={pwSaving}
                  className="w-full sm:w-auto px-5 py-2.5 rounded-xl text-white font-medium disabled:opacity-70 disabled:cursor-not-allowed bg-slate-800 hover:bg-slate-700"
                >
                  {pwSaving ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Profile;
